import { useCallback, useEffect, useState } from 'react';
import {
  fetchFundingReadiness,
  fetchFundingReadinessChecks,
  runFundingReadiness,
} from '../../services/funding/fundingReadinessService';

export function useFundingReadiness({ clientId, agencyId, autoLoad = true } = {}) {
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!clientId || !agencyId) {
      setCheck(null);
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await fetchFundingReadiness(clientId, agencyId);
      setCheck(result);
      return result;
    } catch (err) {
      if (err?.status === 404) {
        setCheck(null);
        return null;
      }
      setError(err?.message || 'Failed to load readiness');
      setCheck(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [clientId, agencyId]);

  const run = useCallback(async () => {
    if (!clientId || !agencyId) return null;

    setRunning(true);
    setError(null);
    try {
      const result = await runFundingReadiness(clientId, agencyId);
      setCheck(result);
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to run readiness scoring');
      throw err;
    } finally {
      setRunning(false);
    }
  }, [clientId, agencyId]);

  useEffect(() => {
    if (autoLoad) {
      load();
    }
  }, [autoLoad, load]);

  return { check, loading, running, error, load, run, setCheck };
}

export function useFundingReadinessList({ agencyId, limit = 50 } = {}) {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFundingReadinessChecks({ agencyId, limit });
      setChecks(result.checks);
      return result.checks;
    } catch (err) {
      setError(err?.message || 'Failed to load readiness checks');
      setChecks([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [agencyId, limit]);

  useEffect(() => {
    load();
  }, [load]);

  return { checks, loading, error, reload: load };
}
